/**
 * Copyright 2022 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import * as React from "react";
import { ObservableQuery } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Button, Description, Input, Label, Sidebar } from "@nordcloud/gnui";
import { CustomerBasic } from "~/generated/graphql";
import { Fieldset } from "~/components/Forms";
import { useCreateCustomer } from "~/views/customers/components/Form/hooks/useCreateCustomer";
import { useUpdateCustomer } from "~/views/customers/components/Form/hooks/useUpdateCustomer";
import {
  CustomerFormSchema,
  CustomerFormSchemaType,
} from "~/views/customers/components/Form/schema";

type CustomerFormProps = {
  customer?: CustomerBasic;
  refetch: ObservableQuery["refetch"];
  isSidebarOpen: boolean;
  setIsSidebarOpen: () => void;
};

function CustomerForm({
  customer,
  refetch,
  isSidebarOpen,
  setIsSidebarOpen,
}: CustomerFormProps) {
  const isEditMode = customer !== undefined;

  const formMethods = useForm<CustomerFormSchemaType>({
    resolver: zodResolver(CustomerFormSchema),
    defaultValues: {
      name: customer?.name,
      rpo: customer?.backupPoliciesBoundary.minRPOInHours,
      primarySnapshotsRetentionQuantity:
        customer?.backupPoliciesBoundary.maxPrimarySnapshotRetentionQuantity,
      primarySnapshotsRetentionInDays:
        customer?.backupPoliciesBoundary.maxPrimarySnapshotRetentionInDays,
      replicaSnapshotsRetentionQuantity:
        customer?.backupPoliciesBoundary.maxReplicaSnapshotRetentionQuantity,
      replicaSnapshotsRetentionInDays:
        customer?.backupPoliciesBoundary.maxReplicaSnapshotRetentionInDays,
    },
  });

  const { formState, register, handleSubmit } = formMethods;

  const onSuccess = () => {
    setIsSidebarOpen();
    refetch();
  };

  const onError = () => {
    setIsSidebarOpen();
  };

  const [createCustomer, isLoadingCreateCustomer] = useCreateCustomer({
    onSuccess: onSuccess,
    onError: onError,
  });
  const [updateCustomer, isLoadingUpdateCustomer] = useUpdateCustomer({
    onSuccess: onSuccess,
    onError: onError,
  });

  const onSubmit = async (data: CustomerFormSchemaType) => {
    isEditMode
      ? await updateCustomer(customer?.id || "", data)
      : await createCustomer(data);
  };

  return (
    <>
      <Sidebar
        title={isEditMode ? "Edit Customer" : "Add customer"}
        isOpen={isSidebarOpen}
        onClick={() => {
          setIsSidebarOpen();
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Fieldset>
            <Label name="Name" htmlFor="name" required />
            <Input
              id="name"
              type="text"
              status={formState.errors.name && "danger"}
              placeholder="Name"
              {...register("name")}
            />
            {formState.errors.name && (
              <Description>{formState.errors.name.message}</Description>
            )}
          </Fieldset>
          <Fieldset>
            <Label name="Min. RPO (in hours)" htmlFor="rpo" required />
            <Input
              id="rpo"
              type="number"
              status={formState.errors.rpo && "danger"}
              placeholder="4"
              {...register("rpo", {
                valueAsNumber: true,
              })}
            />
            {formState.errors.rpo && (
              <Description>{formState.errors.rpo.message}</Description>
            )}
          </Fieldset>
          <Fieldset>
            <Label
              name="Max. primary snapshots retention (quantity)"
              htmlFor="primarySnapshotsRetentionQuantity"
              required
            />
            <Input
              id="primarySnapshotsRetentionQuantity"
              type="number"
              status={
                formState.errors.primarySnapshotsRetentionQuantity && "danger"
              }
              placeholder="1"
              {...register("primarySnapshotsRetentionQuantity", {
                valueAsNumber: true,
              })}
            />
            {formState.errors.primarySnapshotsRetentionQuantity && (
              <Description>
                {formState.errors.primarySnapshotsRetentionQuantity.message}
              </Description>
            )}
          </Fieldset>
          <Fieldset>
            <Label
              name="Max. primary snapshots retention (days)"
              htmlFor="primarySnapshotsRetentionInDays"
              required
            />
            <Input
              id="primarySnapshotsRetentionInDays"
              type="number"
              status={
                formState.errors.primarySnapshotsRetentionInDays && "danger"
              }
              placeholder="1"
              {...register("primarySnapshotsRetentionInDays", {
                valueAsNumber: true,
              })}
            />
            {formState.errors.primarySnapshotsRetentionInDays && (
              <Description>
                {formState.errors.primarySnapshotsRetentionInDays.message}
              </Description>
            )}
          </Fieldset>
          <Fieldset>
            <Label
              name="Max. replica snapshots retention (quantity)"
              htmlFor="replicaSnapshotsRetentionQuantity"
              required
            />
            <Input
              id="replicaSnapshotsRetentionQuantity"
              type="number"
              status={
                formState.errors.replicaSnapshotsRetentionQuantity && "danger"
              }
              placeholder="1"
              {...register("replicaSnapshotsRetentionQuantity", {
                valueAsNumber: true,
              })}
            />
            {formState.errors.replicaSnapshotsRetentionQuantity && (
              <Description>
                {formState.errors.replicaSnapshotsRetentionQuantity.message}
              </Description>
            )}
          </Fieldset>
          <Fieldset>
            <Label
              name="Max. replica snapshots retention (days)"
              htmlFor="replicaSnapshotsRetentionInDays"
              required
            />
            <Input
              id="replicaSnapshotsRetentionInDays"
              type="number"
              status={
                formState.errors.replicaSnapshotsRetentionInDays && "danger"
              }
              placeholder="1"
              {...register("replicaSnapshotsRetentionInDays", {
                valueAsNumber: true,
              })}
            />
            {formState.errors.replicaSnapshotsRetentionInDays && (
              <Description>
                {formState.errors.replicaSnapshotsRetentionInDays.message}
              </Description>
            )}
          </Fieldset>
          <Button
            type="submit"
            disabled={isLoadingCreateCustomer || isLoadingUpdateCustomer}
          >
            Save
          </Button>
        </form>
      </Sidebar>
    </>
  );
}

export { CustomerForm };
